import { useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Toast } from 'primereact/toast';
import { useGroup, GroupDetail, ApiErrorDisplay, LoadingSpinner } from '@authifi/clique-react-ui';
import { EmailTemplateSelector, INVITATION_EMAIL_CATEGORY } from '@authifi/email-templates';

export function GroupDetailPage() {
  const { groupId } = useParams<{ groupId: string }>();
  const navigate = useNavigate();
  const toast = useRef<Toast>(null);
  const numericGroupId = Number.parseInt(groupId ?? '', 10);
  const hasValidGroupId = Number.isInteger(numericGroupId) && numericGroupId > 0;
  const { data: group, isLoading, isError, error } = useGroup(hasValidGroupId ? numericGroupId : 0);

  const handleBack = useCallback(() => {
    navigate('/groups');
  }, [navigate]);

  const handleDeleted = useCallback(() => {
    setTimeout(() => navigate('/groups'), 1500);
  }, [navigate]);

  const handleSuccess = useCallback((message: string) => {
    toast.current?.show({ severity: 'success', summary: 'Success', detail: message, life: 3000 });
  }, []);

  const handleError = useCallback((message: string) => {
    toast.current?.show({ severity: 'error', summary: 'Error', detail: message, life: 5000 });
  }, []);

  if (!hasValidGroupId) {
    return (
      <ApiErrorDisplay
        error={new Error('Invalid group identifier')}
        resourceLabel="group"
        onRetry={handleBack}
        retryLabel="Back to Groups"
      />
    );
  }

  if (isLoading) {
    return <LoadingSpinner message="Loading group..." />;
  }

  if (isError) {
    return (
      <ApiErrorDisplay
        error={error}
        resourceLabel="group"
        onRetry={handleBack}
        retryLabel="Back to Groups"
      />
    );
  }

  if (!group) return null;

  return (
    <div>
      <Toast ref={toast} />
      <GroupDetail
        group={group}
        onBack={handleBack}
        onDeleted={handleDeleted}
        onSuccess={handleSuccess}
        onError={handleError}
        renderInviteTemplateSelector={({ value, onChange, disabled }) => (
          <EmailTemplateSelector
            value={value}
            onChange={onChange}
            disabled={disabled}
            categoryLabel={INVITATION_EMAIL_CATEGORY}
          />
        )}
      />
    </div>
  );
}
